import Button from 'devextreme-react/button';
import { useEffect, useMemo, useState } from 'react';
import { RecordEditDiscussionPopup, opsGridFormPopupSizeKey } from '../opsDataGrid';
import { PatientDetailView } from './PatientDetailView';
import { PatientDuplicateError, patientTrackingApi } from './patientTrackingApi';
import type {
  CoveredEntityDto,
  OurPharmacyDto,
  PatientDto,
  PatientDuplicateMatch,
  PatientUpsertBody
} from './patientTrackingTypes';
import './patientTracking.css';

export interface PatientFormPopupProps {
  visible: boolean;
  /** When set, the form pre-fills from this record and saves as an update. */
  patient: PatientDto | null;
  /** Optional starting values for a brand-new patient (e.g. typed name from a tracker lookup). */
  initial?: Partial<PatientUpsertBody>;
  pharmacies: OurPharmacyDto[];
  coveredEntities: CoveredEntityDto[];
  onClose: () => void;
  /** Fired with the saved patient. The caller is responsible for refreshing/selecting it. */
  onSaved: (patient: PatientDto) => void;
  onDeleted?: (id: string) => void;
  /** Storage key for the popup size; defaults to the patients grid key. */
  sizeStorageKey?: string;
  currentUserId: string;
  currentUserName: string;
  currentUserAliases?: Iterable<string>;
  viewMode?: boolean;
  onRequestEdit?: () => void;
}

const emptyForm = (): PatientUpsertBody => ({
  pharmacyId: '',
  firstName: '',
  lastName: '',
  dob: null,
  phone: null,
  email: null,
  addressLine1: null,
  addressLine2: null,
  city: null,
  state: null,
  postalCode: null,
  homeCoveredEntityId: null,
  enrollmentForm: false,
  notes: null,
  isActive: true,
  rejectIfDuplicate: false
});

function patientToBody(p: PatientDto): PatientUpsertBody {
  return {
    pharmacyId: p.pharmacyId,
    firstName: p.firstName,
    lastName: p.lastName,
    dob: p.dob,
    phone: p.phone,
    email: p.email,
    addressLine1: p.addressLine1,
    addressLine2: p.addressLine2,
    city: p.city,
    state: p.state,
    postalCode: p.postalCode,
    homeCoveredEntityId: p.homeCoveredEntityId,
    enrollmentForm: p.enrollmentForm,
    notes: p.notes,
    isActive: p.isActive,
    rejectIfDuplicate: false
  };
}

/**
 * Patient master add/edit popup used by the Patients grid and the inline-create flow on the
 * tracker. Implements the soft-duplicate warning: on first save, asks the server to reject if
 * duplicates exist; if it does, shows the candidates with "use existing" or "save anyway".
 */
export function PatientFormPopup({
  visible,
  patient,
  initial,
  pharmacies,
  coveredEntities,
  onClose,
  onSaved,
  onDeleted,
  sizeStorageKey = opsGridFormPopupSizeKey('patients-grid'),
  currentUserId,
  currentUserName,
  currentUserAliases,
  viewMode = false,
  onRequestEdit
}: PatientFormPopupProps) {
  const [form, setForm] = useState<PatientUpsertBody>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [duplicates, setDuplicates] = useState<PatientDuplicateMatch[]>([]);

  useEffect(() => {
    if (!visible) return;
    setError(null);
    setDuplicates([]);
    if (patient) {
      setForm(patientToBody(patient));
    } else {
      setForm({ ...emptyForm(), ...(initial ?? {}) });
    }
  }, [visible, patient, initial]);

  const pharmacyOptions = useMemo(
    () => pharmacies.map((p) => ({ id: p.id, name: p.pharmacyName })),
    [pharmacies]
  );
  const ceOptions = useMemo(
    () =>
      coveredEntities
        .filter((c) => !form.pharmacyId || c.pharmacyId === form.pharmacyId)
        .map((c) => ({ id: c.id, name: c.coveredEntityName })),
    [coveredEntities, form.pharmacyId]
  );

  const trimAll = (body: PatientUpsertBody): PatientUpsertBody => ({
    ...body,
    firstName: body.firstName.trim(),
    lastName: body.lastName.trim(),
    phone: body.phone?.trim() || null,
    email: body.email?.trim() || null,
    addressLine1: body.addressLine1?.trim() || null,
    addressLine2: body.addressLine2?.trim() || null,
    city: body.city?.trim() || null,
    state: body.state?.trim() || null,
    postalCode: body.postalCode?.trim() || null,
    notes: body.notes?.trim() || null
  });

  const submit = async (rejectIfDuplicate: boolean) => {
    setSaving(true);
    setError(null);
    try {
      const body = trimAll({ ...form, rejectIfDuplicate });
      if (!body.firstName) throw new Error('First name is required.');
      if (!body.lastName) throw new Error('Last name is required.');
      if (!body.pharmacyId) throw new Error('Select a pharmacy.');
      const result = patient
        ? await patientTrackingApi.updatePatient(patient.id, body)
        : await patientTrackingApi.createPatient(body);
      onSaved(result.patient);
      onClose();
    } catch (e) {
      if (e instanceof PatientDuplicateError) {
        setDuplicates(e.possibleDuplicates);
        setError('Possible duplicate patient(s) found. Use an existing patient or save anyway.');
      } else {
        setError(e instanceof Error ? e.message : 'Save failed.');
      }
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!patient || !onDeleted) return;
    if (!window.confirm(`Delete patient "${patient.fullName}"?`)) return;
    setSaving(true);
    setError(null);
    try {
      await patientTrackingApi.deletePatient(patient.id);
      onDeleted(patient.id);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed.');
    } finally {
      setSaving(false);
    }
  };

  const recordLabel = patient
    ? `Patient: ${patient.lastName}, ${patient.firstName}`
    : 'Patient record';

  return (
    <RecordEditDiscussionPopup
      visible={visible}
      viewMode={viewMode}
      onRequestEdit={onRequestEdit}
      title={
        patient == null ? 'Add patient' : viewMode ? 'View patient' : 'Edit patient'
      }
      onClose={onClose}
      width={1040}
      sizeStorageKey={sizeStorageKey}
      error={error}
      discussionTargetType="patient"
      discussionRecordId={patient?.id ?? null}
      currentUserId={currentUserId}
      currentUserName={currentUserName}
      currentUserAliases={currentUserAliases}
      discussionRecordLabel={recordLabel}
      footer={
        <div className="patient-tracking-popup-footer">
          <div>
            {patient && onDeleted ? (
              <Button text="Delete" type="danger" disabled={saving} onClick={() => void remove()} />
            ) : null}
          </div>
          <div className="patient-tracking-popup-footer__actions">
            <Button text="Cancel" disabled={saving} onClick={onClose} />
            {duplicates.length > 0 ? (
              <Button text="Save anyway" disabled={saving} onClick={() => void submit(false)} />
            ) : (
              <Button
                text={saving ? 'Saving…' : 'Save'}
                type="default"
                disabled={saving}
                onClick={() => void submit(true)}
              />
            )}
          </div>
        </div>
      }
    >
      {viewMode && patient ? (
        <PatientDetailView patient={patient} />
      ) : null}

      {!viewMode && duplicates.length > 0 ? (
        <div className="patient-form-duplicates">
          <div className="patient-form-duplicates__title">Possible duplicates</div>
          <ul>
            {duplicates.map((d) => (
              <li key={d.id}>
                <strong>{d.fullName}</strong>
                {d.dob ? ` — DOB ${d.dob}` : ' — no DOB on file'}
                {d.homeCoveredEntityName ? ` · ${d.homeCoveredEntityName}` : ''}
                {d.openIssues > 0 ? ` · ${d.openIssues} open issue(s)` : ''}
              </li>
            ))}
          </ul>
          <div className="patient-form-duplicates__hint">
            Use one of these existing patients instead of creating a new record, or click
            “Save anyway” if this really is a different person.
          </div>
        </div>
      ) : null}

      {!viewMode ? (
      <div className="patient-form-grid">
        <label>
          <span className="patient-form-label patient-form-label--required">Pharmacy</span>
          <select
            className="patient-form-native-control"
            value={form.pharmacyId}
            onChange={(e) =>
              setForm((f) =>
                f.pharmacyId === e.target.value
                  ? f
                  : { ...f, pharmacyId: e.target.value, homeCoveredEntityId: null }
              )
            }
          >
            <option value="">Select pharmacy...</option>
            {pharmacyOptions.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className="patient-form-label patient-form-label--required">First name</span>
          <input
            className="patient-form-native-control"
            value={form.firstName}
            onChange={(e) =>
              setForm((f) => (f.firstName === e.target.value ? f : { ...f, firstName: e.target.value }))
            }
          />
        </label>
        <label>
          <span className="patient-form-label patient-form-label--required">Last name</span>
          <input
            className="patient-form-native-control"
            value={form.lastName}
            onChange={(e) =>
              setForm((f) => (f.lastName === e.target.value ? f : { ...f, lastName: e.target.value }))
            }
          />
        </label>

        <div className="patient-form-divider" />
        <div className="patient-form-group-label">Demographics</div>
        <label>
          <span className="patient-form-label">DOB</span>
          <input
            className="patient-form-native-control"
            type="date"
            value={form.dob ?? ''}
            onChange={(e) =>
              setForm((f) => {
                const dob = e.target.value || null;
                return f.dob === dob ? f : { ...f, dob };
              })
            }
          />
        </label>
        <label>
          <span className="patient-form-label">Home covered entity</span>
          <select
            className="patient-form-native-control"
            value={form.homeCoveredEntityId ?? ''}
            onChange={(e) =>
              setForm((f) => {
                const homeCoveredEntityId = e.target.value || null;
                return f.homeCoveredEntityId === homeCoveredEntityId ? f : { ...f, homeCoveredEntityId };
              })
            }
          >
            <option value="">None (set per-issue)</option>
            {ceOptions.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label className="patient-form-native-check">
          <input
            type="checkbox"
            checked={form.enrollmentForm}
            onChange={(e) =>
              setForm((f) =>
                f.enrollmentForm === e.target.checked ? f : { ...f, enrollmentForm: e.target.checked }
              )
            }
          />
          <span>Enrollment form on file</span>
        </label>
        <label className="patient-form-native-check">
          <input
            type="checkbox"
            checked={form.isActive}
            onChange={(e) =>
              setForm((f) => (f.isActive === e.target.checked ? f : { ...f, isActive: e.target.checked }))
            }
          />
          <span>Active patient</span>
        </label>

        <div className="patient-form-divider" />
        <div className="patient-form-group-label">Contact</div>
        <label>
          <span className="patient-form-label">Phone</span>
          <input
            className="patient-form-native-control"
            value={form.phone ?? ''}
            onChange={(e) =>
              setForm((f) => ((f.phone ?? '') === e.target.value ? f : { ...f, phone: e.target.value }))
            }
          />
        </label>
        <label>
          <span className="patient-form-label">Email</span>
          <input
            className="patient-form-native-control"
            type="email"
            value={form.email ?? ''}
            onChange={(e) =>
              setForm((f) => ((f.email ?? '') === e.target.value ? f : { ...f, email: e.target.value }))
            }
          />
        </label>

        <div className="patient-form-divider" />
        <div className="patient-form-group-label">Address</div>
        <label className="patient-form-grid__full">
          <span className="patient-form-label">Address line 1</span>
          <input
            className="patient-form-native-control"
            value={form.addressLine1 ?? ''}
            onChange={(e) =>
              setForm((f) =>
                (f.addressLine1 ?? '') === e.target.value ? f : { ...f, addressLine1: e.target.value }
              )
            }
          />
        </label>
        <label>
          <span className="patient-form-label">Address line 2</span>
          <input
            className="patient-form-native-control"
            value={form.addressLine2 ?? ''}
            onChange={(e) =>
              setForm((f) =>
                (f.addressLine2 ?? '') === e.target.value ? f : { ...f, addressLine2: e.target.value }
              )
            }
          />
        </label>
        <label>
          <span className="patient-form-label">City</span>
          <input
            className="patient-form-native-control"
            value={form.city ?? ''}
            onChange={(e) =>
              setForm((f) => ((f.city ?? '') === e.target.value ? f : { ...f, city: e.target.value }))
            }
          />
        </label>
        <label>
          <span className="patient-form-label">State</span>
          <input
            className="patient-form-native-control"
            value={form.state ?? ''}
            onChange={(e) =>
              setForm((f) => ((f.state ?? '') === e.target.value ? f : { ...f, state: e.target.value }))
            }
          />
        </label>
        <label>
          <span className="patient-form-label">Postal code</span>
          <input
            className="patient-form-native-control"
            value={form.postalCode ?? ''}
            onChange={(e) =>
              setForm((f) =>
                (f.postalCode ?? '') === e.target.value ? f : { ...f, postalCode: e.target.value }
              )
            }
          />
        </label>

        <div className="patient-form-divider" />
        <label className="patient-form-grid__full">
          <span className="patient-form-label">Patient notes</span>
          <textarea
            className="patient-form-native-control patient-form-native-control--textarea patient-form-textarea--lg"
            value={form.notes ?? ''}
            placeholder="General notes about this patient (independent of any one issue)"
            onChange={(e) =>
              setForm((f) => ((f.notes ?? '') === e.target.value ? f : { ...f, notes: e.target.value }))
            }
          />
        </label>
      </div>
      ) : null}
    </RecordEditDiscussionPopup>
  );
}
